import { useState, useEffect } from "react";
import EventButton from "./EventButton";

const passOptionsCommon = [
  "clearance",
  "into_pen_area",
  "corner",
  "free_kick",
  "penalty",
  "throwing",
];

const passOptionsFail = ["interception", ...passOptionsCommon];

const lightModePassOptionsSuccess = [
  "cross",
  "forward",
  "back",
  "sideway",
];
const lightModePassOptionsFail = ["cross", "forward", "back", "sideway"];

const shotOptionsSuccess = [
  "goal",
  "by_head",
  "save",
  "corner",
  "free_kick",
  "penalty",
];

const shotOptionsFail = [
  "blocked",
  "by_head",
  "corner",
  "free_kick",
  "penalty",
];

const packingOptions = Array.from({ length: 10 }, (_, i) => ({
  name: (i + 1).toString(),
  key: i === 9 ? "0" : (i + 1).toString(), // "10" uses "0" key
  displayName: (i + 1).toString(),
}));

const normalKeys = {
  pass: {
    key: "1",
    options: [
      {
        name: "interception",
        key: "1",
      },
      {
        name: "clearance",
        key: "2",
      },
      {
        name: "into_pen_area",
        key: "3",
        displayName: "Into Pen Area",
      },
      {
        name: "packing",
        key: "4",
      },
      {
        name: "corner",
        key: "5",
      },
      {
        name: "free_kick",
        key: "6",
      },
      {
        name: "penalty",
        key: "7",
      },
      {
        name: "throwing",
        key: "8",
      },
      // Light mode options
      {
        name: "cross",
        key: "4",
      },
      {
        name: "key_pass",
        key: "Y",
      },
      {
        name: "assist",
        key: "U",
      },
      {
        name: "forward",
        key: "J",
      },
      {
        name: "back",
        key: "K",
      },
      {
        name: "sideway",
        key: "L",
      },
      // Packing options for pass
      ...packingOptions,
    ],
  },
  shot: {
    key: "5",
    options: [
      {
        name: "goal",
        key: "1",
      },
      {
        name: "blocked",
        key: "2",
        displayName: "Block Shot",
      },
      {
        name: "by_head",
        key: "3",
        displayName: "Headshot",
      },
      {
        name: "save",
        key: "4",
      },
      {
        name: "corner",
        key: "5",
      },
      {
        name: "free_kick",
        key: "6",
      },
      {
        name: "penalty",
        key: "7",
      },
    ],
  },
  duel: {
    key: "6",
    options: [{ name: "ariel_duel", key: "8", displayName: "Ariel" }],
  },
  success_duel: {
    key: "6",
    options: [{ name: "ariel_duel", key: "8", displayName: "Ariel" }],
  },
  ball_control: {
    key: "7",
    options: [
      { name: "tech_mistake", key: "1" },
      { name: "in_pen_area", key: "2" },
    ],
  },
  game_stops: {
    key: "8",
    options: [
      { name: "foul", key: "1" },
      { name: "offside", key: "2" },
    ],
  },
  dribble: {
    key: "3",
    options: [
      { name: "packing", key: "4" },
      // Packing options for dribble
      ...packingOptions,
    ],
  },
};

const EventControls = ({ addNewEvent, onEventSelected, lightMode, isSingleTeam, pipelineType }) => {
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentStep, setCurrentStep] = useState("initial");
  const [selectedOptions, setSelectedOptions] = useState({});
  const [canAddEvent, setCanAddEvent] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [multipleSelections, setMultipleSelections] = useState(new Set());
  const [isFinalStep, setIsFinalStep] = useState(false);
  const [keys, setKeys] = useState(normalKeys);

  // Move passOptionsSuccess and dribbleOptionsSuccess here so they can use isSingleTeam
  const passOptionsSuccess = isSingleTeam && !["team_analytics", "player_analytics"].includes(pipelineType)
    ? [...passOptionsCommon, "packing"]
    : [...passOptionsCommon];
  const dribbleOptionsSuccess = isSingleTeam && !["team_analytics", "player_analytics"].includes(pipelineType)
    ? ["packing"]
    : [];

  // Helper function to check if current step is final
  const checkIsFinalStep = (event, step) => {
    console.log("checkIsFinalstep", { event, step });
    return (
      (event === "pass" && ["success", "fail"].includes(step)) ||
      (event === "dribble" && ["success", "fail"].includes(step)) ||  // Add dribble events
      (event === "shot" && ["success", "fail"].includes(step)) ||
      event === "success_duel" ||
      (lightMode && event === "duel" && ["success", "fail"].includes(step)) ||
      event === "foul"
    );
  };

  const handleAddEvent = () => {
    addNewEvent(
      currentEvent,
      selectedOptions,
      multipleSelections,
      handleEscape
    );
  };

  // Create dynamic event flows based on light mode
  const getEventFlows = () => {
    // Conditionally add key_pass and assist only for player_analytics pipeline
    const playerAnalyticsOptions = pipelineType === "player_analytics" ? ["key_pass", "assist"] : [];
    const lightModePassOptionsSuccessWithCondition = [
      ...lightModePassOptionsSuccess,
      ...playerAnalyticsOptions
    ];

    console.log({lightMode})
    const baseFlows = {
      pass: {
        initial: ["success", "fail"],
        success: lightMode
          ? [...passOptionsSuccess, ...lightModePassOptionsSuccessWithCondition]
          : passOptionsSuccess,
        fail: lightMode
          ? [...passOptionsFail, ...lightModePassOptionsFail]
          : passOptionsFail,
        packing: isSingleTeam ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] : null,
      },
      dribble: {
        initial: ["success", "fail"],
        success: dribbleOptionsSuccess,
        fail: null,
        packing: isSingleTeam ? ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"] : null,
      },
      recovery: {
        initial: null,
        success: null,
        fail: null,
      },
      interception: {
        initial: null,
        success: null,
        fail: null,
      },
      tackle: {
        initial: ["success", "fail"],
        success: null,
        fail: null,
      },
      shot: {
        initial: ["success", "fail"],
        success: shotOptionsSuccess,
        fail: shotOptionsFail,
      },
      ...(lightMode
        ? {
            duel: {
              initial: ["success", "fail"],
              success: ["ariel_duel"],
              fail: ["ariel_duel"],
            },
          }
        : {
            success_duel: {
              initial: ["ariel_duel"],
              success: null,
              fail: null,
            },
          }),
      ball_control: {
        initial: ["tech_mistake", "in_pen_area"],
        success: null,
        fail: null,
      },
      game_stops: {
        initial: ["foul", "offside"],
        success: null,
        fail: null,
      },
      save: {
        initial: null,
        success: null,
        fail: null,
      },
      auto_goal: {
        initial: null,
        success: null,
        fail: null,
      },
    };

    return baseFlows;
  };

  // Create dynamic event buttons based on lightMode
  const getEventButtons = () => {
    const baseButtons = [
      { label: "Hide", key: "Escape", value: "esc" },
      { label: "Pass", key: "1", value: "pass" },
      { label: "Recovery", key: "2", value: "recovery" },
      { label: "Dribble", key: "3", value: "dribble" },
      { label: "Tackle", key: "4", value: "tackle" },
      { label: "Shot", key: "5", value: "shot" },
      {
        label: lightMode ? "Duel" : "Success Duel",
        key: "6",
        value: lightMode ? "duel" : "success_duel",
      },
      { label: "Ball Control", key: "7", value: "ball_control" },
      { label: "Game stops", key: "8", value: "game_stops" },
      { label: "Auto goal", key: "P", value: "auto_goal" },
    ];

    return baseButtons;
  };

  const handleEventClick = (eventType) => {
    setCurrentEvent(eventType);
    setCurrentStep("initial");
    setSelectedOptions({});
    setIsFinalStep(false);

    // Notify parent component that an event has been selected
    if (onEventSelected) {
      onEventSelected(true);

      // Capture the current time as the start time
      if (window.videoRef && window.videoRef.current) {
        window.startTime = window.videoRef.current.currentTime;
      }
    }

    // If the event has no flow (all nulls), enable Add Event immediately
    const flows = getEventFlows();
    if (flows[eventType]?.initial === null) {
      setCanAddEvent(true);
      setCurrentStep(null);
    } else if (eventType === "success_duel") {
      setCanAddEvent(true);
      setIsFinalStep(checkIsFinalStep(eventType));
    } else {
      setCanAddEvent(false);
    }
  };

  const handleOptionClick = (option) => {
    console.log(`handleOptionClick called with option: ${option}`);
    console.log(
      `Current state - event: ${currentEvent}, step: ${currentStep}, isFinalStep: ${isFinalStep}`
    );

    if (isFinalStep) {
      // Special case: If pass > success > packing, or dribble > success > packing, go to packing step
      if (
        ((currentEvent === "pass" && currentStep === "success") ||
          (currentEvent === "dribble" && currentStep === "success")) &&
        option === "packing"
      ) {
        if (!isSingleTeam) return; // Prevent transition if not single team mode
        setCurrentStep("packing");
        setIsFinalStep(false);
        setCanAddEvent(false);
        return;
      }
      console.log(
        `In final step, toggling option: ${option} in multipleSelections`
      );
      // Toggle the selection in multipleSelections
      setMultipleSelections((prev) => {
        const newSelections = new Set(prev);
        if (newSelections.has(option)) {
          console.log(`Removing ${option} from selections`);
          newSelections.delete(option);
        } else {
          console.log(`Adding ${option} to selections`);
          newSelections.add(option);
        }
        return newSelections;
      });
      setCanAddEvent(true);
    } else {
      const flows = getEventFlows();
      console.log(
        `Not in final step, adding ${option} to selectedOptions for step ${currentStep}`
      );
      console.log(`Current selectedOptions:`, selectedOptions);

      setSelectedOptions((prev) => ({
        ...prev,
        [currentStep]: option,
      }));

      const nextOptions = flows[currentEvent]?.[option];
      console.log(`Next options for ${currentEvent}.${option}:`, nextOptions);

      if (typeof nextOptions === "function") {
        console.log(`nextOptions is a function, evaluating...`);
        const options = nextOptions(selectedOptions);
        console.log(`Function returned options:`, options);
        if (options.length > 0) {
          setCurrentStep(option);
          setMultipleSelections(new Set());
          const newIsFinalStep = checkIsFinalStep(currentEvent, option);
          console.log(
            `Setting new step: ${option}, isFinalStep: ${newIsFinalStep}`
          );
          setIsFinalStep(newIsFinalStep);
          if (newIsFinalStep) {
            console.log(`Final step reached, enabling Add Event button`);
            setCanAddEvent(true);
          }
        }
      } else if (nextOptions === null) {
        console.log(
          `nextOptions is null, ending flow and enabling Add Event button`
        );
        setCanAddEvent(true);
        setCurrentStep(null);
      } else if (nextOptions) {
        console.log(
          `Moving to next step: ${option}, options available:`,
          nextOptions
        );
        setCurrentStep(option);
        setMultipleSelections(new Set());
        const newIsFinalStep = checkIsFinalStep(currentEvent, option);
        console.log(`Checking if final step: ${newIsFinalStep}`);
        setIsFinalStep(newIsFinalStep);
        if (newIsFinalStep) {
          console.log(`Final step reached, enabling Add Event button`);
          setCanAddEvent(true);
        }
      } else {
        console.log(
          `No next options, ending flow and enabling Add Event button`
        );
        setCanAddEvent(true);
        setCurrentStep(null);
      }
    }
  };

  const handleEscape = () => {
    if (currentEvent || Object.keys(selectedOptions).length > 0) {
      // Clear current selections
      setCurrentEvent(null);
      setCurrentStep("initial");
      setSelectedOptions({});
      setCanAddEvent(false);
      setMultipleSelections(new Set()); // Clear multiple selections

      // Notify parent that event selection has been cleared
      if (onEventSelected) {
        onEventSelected(false);
      }
    } else {
      // Hide the component
      setIsVisible(false);
    }
  };

  const getOptionKey = (eventType, optionName, currentStepOverride) => {
    // Special case for pass/dribble > packing step
    if ((eventType === "pass" || eventType === "dribble") && (currentStepOverride || currentStep) === "packing") {
      return optionName;
    }
    if (optionName === "success") return "+";
    else if (optionName === "fail") return "-";

    const eventKeys = keys[eventType]?.options;
    return eventKeys?.find((option) => option.name === optionName)?.key;
  };

  // Added helper function to get display name for options
  const getOptionDisplayName = (eventType, optionName, currentStepOverride) => {
    // Special case for pass/dribble > packing step
    if ((eventType === "pass" || eventType === "dribble") && (currentStepOverride || currentStep) === "packing") {
      return optionName;
    }
    const eventKeys = keys[eventType]?.options;
    const option = eventKeys?.find((option) => option.name === optionName);
    return (
      option?.displayName ||
      optionName.charAt(0).toUpperCase() + optionName.slice(1)
    );
  };

  const renderSelectedOptions = () => {
    if (!currentEvent && Object.keys(selectedOptions).length === 0) return null;

    return (
      <div>
        <div className="tw-rounded-md tw-w-fit tw-border tw-border-solid tw-border-white tw-p-1 tw-grid tw-grid-cols-5 tw-gap-1 tw-bg-white tw-bg-opacity-10">
          {/* Show the initial event */}
          {currentEvent && (
            <span className="tw-p-1 tw-text-xs tw-text-white tw-uppercase tw-text-center tw-min-w-16 tw-bg-[#528048] tw-rounded-md">
              {currentEvent}
            </span>
          )}

          {/* Show selected options in order */}
          {Object.entries(selectedOptions).map(([step, value]) => (
            <span
              key={step}
              className="tw-p-1 tw-text-xs tw-text-white tw-uppercase tw-text-center tw-min-w-16 tw-bg-[#528048] tw-rounded-md"
            >
              {value}
            </span>
          ))}

          {/* Show multiple selections if they exist */}
          {multipleSelections.size > 0 &&
            Array.from(multipleSelections).map((value) => (
              <span
                key={value}
                className="tw-p-1 tw-text-xs tw-text-white tw-uppercase tw-text-center tw-min-w-16 tw-bg-[#528048] tw-rounded-md"
              >
                {value}
              </span>
            ))}
        </div>
      </div>
    );
  };

  const renderCurrentOptions = () => {
    if (!isVisible) {
      return (
        <div className="tw-flex tw-gap-2">
          <EventButton
            label="Show"
            number="Ctrl+E"
            onClick={() => setIsVisible(true)}
          />
        </div>
      );
    }

    if (!currentEvent || !currentStep) {
      const buttons = getEventButtons();
      return (
        <div className="tw-flex tw-gap-2">
          {buttons.map((button) => (
            <EventButton
              key={button.value}
              label={button.label}
              number={button.key}
              onClick={() => {
                if (button.value === "esc") {
                  handleEscape();
                } else if (getEventFlows()[button.value]) {
                  handleEventClick(button.value);
                }
              }}
            />
          ))}
        </div>
      );
    }

    const flows = getEventFlows();
    let options = flows[currentEvent]?.[currentStep];
    if (typeof options === "function") {
      options = options(selectedOptions);
    }
    options = options || [];
    return (
      <div className="tw-flex tw-gap-2">
        <EventButton label="Hide" number="ESC" onClick={handleEscape} />
        {options.map((option, index) => (
          <EventButton
            key={option}
            label={getOptionDisplayName(currentEvent, option, currentStep)}
            number={getOptionKey(currentEvent, option, currentStep)}
            onClick={() => handleOptionClick(option)}
            isSelected={isFinalStep && multipleSelections.has(option)}
          />
        ))}
      </div>
    );
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA"
      ) {
        return;
      }

      // Handle Escape key
      if (event.key === "Escape") {
        handleEscape();
        return;
      }

      // Handle Backspace key to remove last item from history
      if (event.key === "Backspace") {
        // First check if we have multiple selections
        if (multipleSelections.size > 0) {
          // Remove the last added item from multipleSelections
          const selectionsArray = Array.from(multipleSelections);
          const newSelections = new Set(selectionsArray.slice(0, -1));
          setMultipleSelections(newSelections);
          return;
        }

        // If no multiple selections, remove the last step from selectedOptions
        if (Object.keys(selectedOptions).length > 0) {
          const steps = Object.keys(selectedOptions);
          const lastStep = steps[steps.length - 1];

          const newSelectedOptions = { ...selectedOptions };
          delete newSelectedOptions[lastStep];
          setSelectedOptions(newSelectedOptions);

          // If we removed the last step, go back to the previous step
          if (steps.length > 1) {
            setCurrentStep(steps[steps.length - 2]);
            setCanAddEvent(false);
          } else if (currentEvent) {
            // If we're back to just the event, set step to initial
            setCurrentStep("initial");
            setCanAddEvent(false);
          }

          // Reset isFinalStep based on the new state
          setIsFinalStep(
            checkIsFinalStep(currentEvent, steps[steps.length - 2])
          );
          return;
        }

        // If no selections left but we have an event, clear the event
        if (currentEvent) {
          setCurrentEvent(null);
          setCurrentStep("initial");
          setCanAddEvent(false);
          // Notify parent that event selection has been cleared
          if (onEventSelected) {
            onEventSelected(false);
          }
          return;
        }
      }

      // If component is hidden, only respond to Ctrl + E
      if (!isVisible) {
        if (event.ctrlKey && event.key.toLowerCase() === "e") {
          event.preventDefault(); // Prevent default browser behavior
          setIsVisible(true);
        }
        return;
      }

      const flows = getEventFlows();
      // Rest of the existing key handling logic
      if (currentEvent && currentStep) {
        let options = flows[currentEvent]?.[currentStep];
        // if (typeof options === "function") {
        //   options = options(selectedOptions);
        // }
        options = options || [];
        // Special case for pass/dribble > packing step
        if (
          ((currentEvent === "pass" && currentStep === "packing") ||
            (currentEvent === "dribble" && currentStep === "packing"))
        ) {
          // Find the option in normalKeys that matches the pressed key
          const packingOption = keys[currentEvent]?.options?.find(
            (option) =>
              // Only match packing options (name: "1" to "10")
              /^[1-9]$|10/.test(option.name) &&
              option.key === event.key
          );
          if (packingOption) {
            if (!isSingleTeam) return;
            handleOptionClick(packingOption.name);
            return;
          }
        }
        if (
          options.length === 2 &&
          options.includes("success") &&
          options.includes("fail")
        ) {
          if (event.key === "=" || event.key === "+")
            handleOptionClick("success");
          else if (event.key === "-") handleOptionClick("fail");
        }
        const option = keys[currentEvent]?.options?.find(
          (option) => option.key.toLowerCase() === event.key.toLowerCase()
        );
        if (option) {
          handleOptionClick(option.name);
        }
        // const numKey = parseInt(event.key);
        // if (!isNaN(numKey) && numKey > 0 && numKey <= options.length) {
        //   const selectedOption = options[numKey - 1];
        //   handleOptionClick(selectedOption);
        // }
      } else {
        const buttons = getEventButtons();
        const button = buttons.find(
          (btn) => btn.key.toLowerCase() === event.key.toLowerCase()
        );
        if (button && button.value !== "esc") {
          if (flows[button.value]) {
            handleEventClick(button.value);
          }
        }
      }

      if (event.key === "Enter" && canAddEvent) {
        handleAddEvent();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [
    currentEvent,
    currentStep,
    selectedOptions,
    canAddEvent,
    isVisible,
    multipleSelections,
    keys,
    addNewEvent,
    onEventSelected,
    lightMode,
    isSingleTeam,
  ]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {renderSelectedOptions()}
      <div className="tw-flex tw-gap-2">
        {renderCurrentOptions()}
        {isVisible && (
          <EventButton
            label="Add Event"
            number="ENTER"
            isWide={true}
            disabled={!canAddEvent}
            onClick={handleAddEvent}
          />
        )}
      </div>
    </div>
  );
};

export default EventControls;
